import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  ElementRef,
  ViewChild,
  ChangeDetectionStrategy
} from "@angular/core";
import {
  distinct,
  filterBy,
  FilterDescriptor,
} from "@progress/kendo-data-query";
import { FilterService } from "@progress/kendo-angular-grid";
import { CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { GridFieldsService } from '~/services/modules/stocks/gridField';
import { DateFormatPipe } from '~/shared/pipes/dateFormat';

@Component({
    selector: 'app-multicheck-filter',
    templateUrl: './multicheck-filter.component.html',
    styleUrls: ['./multicheck-filter.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class MulticheckFilterComponent implements OnInit, OnChanges {

  @Input() public isPrimitive: boolean;
  @Input() public currentFilter: CompositeFilterDescriptor;
  @Input() public textField: any;
  @Input() public valueField: any;
  @Input() public filterService: FilterService;
  @Input() public page: string;
  @Input() public field: string;
  @Input() public type: any;
  @Input() public isCustomField: boolean;
  @Output() public valueChange = new EventEmitter<any>();
  @Output() public dataChecked = new EventEmitter<boolean>();

  @ViewChild("search") search: ElementRef;

  public data: any;
  public currentData: unknown[];
  public showFilter = true;
  private value: unknown[] = [];
  public searchValue: any;

  public textAccessor = (dataItem: any): string =>
    this.isPrimitive ? dataItem : dataItem[this.textField];
  public valueAccessor = (dataItem: any): unknown =>
    this.isPrimitive ? dataItem : dataItem[this.valueField];

    constructor(
      private _gridFields: GridFieldsService,
      private dateFormatPipe: DateFormatPipe
      ) {
    }

  ngOnInit(): void {
    let filterField = this.field;
    if (filterField === 'personNameFirstLast') {
      filterField = 'person.firstName';
    }
    this._gridFields.getFilterOptions(this.page, filterField, this.isCustomField)
    .subscribe(x => {
      if (x) {
        this.data = x;
        if(this.type == "date" && this.data && this.data.length > 0){
           this.data.forEach((element: any, index: number) => {
            this.data[index] = this.dateFormatPipe.transform(element);
          });
        }

        this.currentData = this.data;
        this.syncValueFromFilter();
        this.showFilter = typeof this.textAccessor(this.currentData[0]) === "string";

        this.dataChecked.emit(true);
      } else {
        this.dataChecked.emit(false);
      }
    }, error => {
      this.dataChecked.emit(false);
    })
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['currentFilter']) {
      this.syncValueFromFilter();
    }
  }

  private syncValueFromFilter(): void {
    if (this.currentFilter && this.currentFilter.filters) {
      this.value = this.currentFilter.filters.map(
        (f: FilterDescriptor) => f.value
      );
    } else {
      this.value = [];
    }
  }

  public isItemSelected(item: unknown): boolean {
    return this.value.some((x) => x === this.valueAccessor(item));
  }

  public get selectedCount(): number {
    if (!this.currentData) return 0;
    return this.currentData.filter((item) => this.isItemSelected(item)).length;
  }

  public get areAllSelected(): boolean {
    if (!this.currentData || this.currentData.length === 0) return false;
    return this.currentData.every((item) => this.isItemSelected(item));
  }

  public get isSomeSelected(): boolean {
    if (!this.currentData || this.currentData.length === 0) return false;
    const count = this.selectedCount;
    return count > 0 && count < this.currentData.length;
  }

  public toggleSelectAll(e?: Event): void {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (this.areAllSelected) {
      const currentValues = this.currentData.map((item) => this.valueAccessor(item));
      this.value = this.value.filter((x) => !currentValues.includes(x));
    } else {
      const currentValues = this.currentData.map((item) => this.valueAccessor(item));
      this.value = Array.from(new Set([...this.value, ...currentValues]));
    }

    this.filterService.filter({
      filters: this.value.map((value) => ({
        field: this.field,
        operator: "eq",
        value,
      })),
      logic: "or",
    });
  }

  public onSelectionChange(item: unknown, li?: HTMLLIElement): void {
    if (this.value.some((x) => x === item)) {
      this.value = this.value.filter((x) => x !== item);
    } else {
      this.value.push(item);
    }

    this.filterService.filter({
      filters: this.value.map((value) => ({
        field: this.field,
        operator: "eq",
        value,
      })),
      logic: "or",
    });

    if (li)
      this.onFocus(li);
  }

  public onInput(e: Event): void {
    const term = (e.target as HTMLInputElement).value;
    this.searchValue = term;
    if (!term || term.trim() === '') {
      this.currentData = this.data;
      return;
    }
    const termLower = term.toLowerCase().trim();
    if (this.isPrimitive) {
      this.currentData = (this.data || []).filter((dataItem: any) =>
        String(this.textAccessor(dataItem)).toLowerCase().includes(termLower)
      );
    } else {
      this.currentData = distinct(
        [
          ...this.currentData.filter((dataItem) =>
            this.value.some((val) => val === this.valueAccessor(dataItem))
          ),
          ...filterBy(this.data, {
            operator: "contains",
            field: this.textField,
            value: term,
          }),
        ],
        this.textField
      );
    }
  }

  public clearSearch(e?: Event): void {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    this.searchValue = '';
    this.currentData = this.data;
    if (this.search?.nativeElement) {
      this.search.nativeElement.value = '';
      this.search.nativeElement.focus();
    }
  }

  public onFocus(li: HTMLLIElement): void {
    const ul = li.parentNode as HTMLUListElement;
    const below =
      ul.scrollTop + ul.offsetHeight < li.offsetTop + li.offsetHeight;
    const above = li.offsetTop < ul.scrollTop;

    // Scroll to focused checkbox
    if (above) {
      ul.scrollTop = li.offsetTop;
    }

    if (below) {
      ul.scrollTop += li.offsetHeight;
    }
  }

  public onSearch() {
    if (this.currentData[this.value.length]) {
      this.onSelectionChange(this.currentData[this.value.length]);
      this.searchValue = this.currentData[this.value.length];

      this.valueChange.emit({field: this.field, value: this.value});
    }
  }
}
